/**
 * The two ways Thunder draws a compliance score: as a ring, and as a pill.
 *
 * The band thresholds (>=90 pass, >=70 warn) come from the engine, not from
 * here — they are part of the scoring contract (DOMAIN-MODEL §7).
 *
 * WHY THERE ARE TWO SHAPES. A ring is a proportion mark: it is worth its space only when
 * the disc inside it is wide enough to hold the number legibly. Below that it has to choose
 * between printing digits on top of its own arc and shrinking them to a smudge, and it used
 * to do both at once (see `MIN_NUMBERED_RING`). So the ring keeps the number only where the
 * number fits, and `ScorePill` carries the score everywhere else — a dense action row, a
 * list, a header. Callers pick by the room they have, not by taste.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type Band = 'pass' | 'warn' | 'fail';

const BAND_VAR: Record<Band, string> = {
  pass: '--success',
  warn: '--warn',
  fail: '--danger',
};

const BAND_LABEL: Record<Band, string> = {
  pass: 'Compliant',
  warn: 'Partially compliant',
  fail: 'Non-compliant',
};

/**
 * The short band word, deliberately the same vocabulary the header's `ComplianceButton`
 * popover uses. Two controls showing the same site's score must not name its band differently.
 */
const BAND_WORD: Record<Band, string> = {
  pass: 'compliant',
  warn: 'partial',
  fail: 'non-compliant',
};

/**
 * Below this diameter a ring does not get to draw a number.
 *
 * THE BUG THIS ENCODES. The site hub header asked for `size={38}`. With the old flat 8px
 * stroke that leaves a clear disc of 38 − 3×8 = **14px**, and the label stack measured
 * **16.5px** — a 10.26px "89" plus a 2px gap plus a 4.18px "%". Measured in the live app
 * (Playwright + getComputedStyle, both themes): the digits' box ran from y=100.8 to 111
 * while the clear disc ran 102 to 116, so the number was printed ON the amber arc, and the
 * "%" below it was a 4.18px glyph — not small text, no text at all. Contrast was never the
 * problem (19.06:1 for the digits in both themes); geometry was. The user's report was
 * "the percentage value in right corner is not visible clearly".
 *
 * 56px is the smallest diameter at which the widest label ("100%") still clears the stroke
 * with the digits at >=15px: stroke 5 → clear disc 41px, digits 15px, "%" 10px.
 * A caller with less room than this wants `ScorePill`, not a ring with a secret.
 */
const MIN_NUMBERED_RING = 56;

export interface ScoreRingProps {
  score: number;
  band: Band;
  size?: number;
  className?: string;
}

export function ScoreRing({
  score,
  band,
  size = 88,
  className,
}: ScoreRingProps): ReactNode {
  /*
   * The stroke was a flat 8px at every size, which is 71% of the diameter of a 34px ring —
   * a solid disc of colour rather than a ring, and no room left inside for anything. Tying
   * it to the diameter keeps a small ring readable AS a ring (its arc is the only thing it
   * still says) while leaving 84px and 88px — Reports and Compliance — on exactly 8px, so
   * those two call sites are pixel-identical to before.
   */
  const stroke = Math.max(3, Math.round(size * 0.0909));
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = c - (clamped / 100) * c;

  /* Clear diameter inside the stroke: the ring's outer edge sits at size/2, its inner edge
   * at size/2 − 1.5·stroke. Everything the label needs has to fit in this, not in `size`. */
  const inner = size - 3 * stroke;
  const numbered = size >= MIN_NUMBERED_RING;

  /* Never below 10px: a "%" scaled as a flat fraction of the diameter was what produced the
   * 4.18px glyph. The digits then take what the disc has left, capped by the width of the
   * widest string the label ever holds — "100", 3 tabular-mono digits at ~0.6em advance. */
  const pctSize = Math.max(10, Math.round(size * 0.11));
  const numSize = Math.round(Math.min(size * 0.27, inner - pctSize - 2, inner / 1.8));

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
      role="img"
      /* Carried whether or not the number is drawn — a ring too small to print the score
         still has to announce it. */
      aria-label={`Compliance score ${clamped} percent — ${BAND_LABEL[band]}`}
      title={`${clamped}% — ${BAND_LABEL[band]}`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--muted)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`var(${BAND_VAR[band]})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset var(--dur-drawer) var(--ease-drawer)',
          }}
        />
      </svg>
      {numbered && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* Explicit --fg rather than inherited: the ring is dropped into action rows and
              list rows whose inherited colour is --muted-fg, and the score is not a
              secondary label.

              Measured WHERE THE DIGITS ACTUALLY SIT, which after the 56 px floor is only the
              Reports hero (84) and Compliance (88) — both on --card, not --bg: 19.90:1 light
              (#09090b on #ffffff) and 17.31:1 dark (#fafafa on rgb(22,22,24)). The figure
              here used to read "19.06:1 in light, 19.06:1 in dark", which was --fg on --bg
              and made the two themes look equal; they are not. Both are far over 4.5:1, but
              this file's whole argument is measured numbers and a wrong one invites the next
              reader to trust headroom that is not there. */}
          <span
            className="lm-num font-bold leading-none"
            style={{ fontSize: numSize, color: 'var(--fg)' }}
          >
            {clamped}
          </span>
          <span
            className="font-medium leading-none"
            style={{ fontSize: pctSize, color: 'var(--muted-fg)', marginTop: 2 }}
          >
            %
          </span>
        </div>
      )}
    </div>
  );
}

export interface ScorePillProps {
  score: number;
  band: Band;
  /** Replaces the default hover text. Pass the pass/warn/fail breakdown where you have it. */
  title?: string | undefined;
  className?: string | undefined;
  /**
   * Names what the number is — "Compliance 83%" — in place of the band word. A bare
   * "83% partial" beside a site name was read as data coverage, not a compliance score
   * (Hamzah, 2026-09-29); the band stays on the mark and in the hover text.
   */
  prefix?: string | undefined;
}

/**
 * The score at text scale: `● 89% partial`.
 *
 * USED TO BE THE HEADER'S OWN SHAPE — `ComplianceBadge` drew exactly this pill inline, so a
 * reader who had learned it in the app bar would not have to learn a second thing on the
 * site page. The header now shows an icon with a dot instead (`ComplianceButton`, to reclaim
 * the width the pill spent), and this same `ScorePill` is what its popover opens into — so
 * the shape did not disappear, it moved one click deeper, and the vocabulary is still shared
 * rather than reinvented per screen. The band is carried by the dot, the border and the
 * tint, and named in words — status is never conveyed by colour alone (DESIGN-SYSTEM §1).
 *
 * WHY THE NUMBER IS NOT BAND-COLOURED. This and `StatusBadge` both used to paint their text
 * in the band colour over an 11-12% tint of that same colour. Measured, that is 4.16:1 for
 * amber (#b45309 on the composite rgb(242,232,224)) in light theme — under the 4.5:1 floor,
 * and the score is the one number on this page a reader must not squint at. So the digits
 * are --fg and the colour lives in the chrome. Measured on the PILL's own tinted composite,
 * which is where these digits sit: 16.44:1 light and 16.02:1 dark. (Not the 19.06/16.03 a
 * previous version of this comment claimed — that pair mixed a --bg measurement with a
 * composite one and contradicted the ring's own note above.)
 * Do not "restore consistency" by tinting the digits; it costs a third of the contrast.
 */
export function ScorePill({
  score,
  band,
  title,
  className,
  prefix,
}: ScorePillProps): ReactNode {
  const clamped = Math.max(0, Math.min(100, score));
  const colour = `var(${BAND_VAR[band]})`;

  return (
    <span
      className={cn(
        'inline-flex h-[32px] shrink-0 items-center gap-[7px] whitespace-nowrap',
        'rounded-[var(--radius-pill)] px-[11px]',
        className,
      )}
      style={{
        border: `1px solid color-mix(in srgb, ${colour} 40%, transparent)`,
        background: `color-mix(in srgb, ${colour} 11%, transparent)`,
      }}
      role="img"
      aria-label={`Compliance score ${clamped} percent — ${BAND_LABEL[band]}`}
      title={title ?? `${clamped}% — ${BAND_LABEL[band]}`}
    >
      <span
        className="size-[7px] shrink-0 rounded-full"
        style={{ background: colour }}
        aria-hidden
      />
      {/* The "%" is part of the number, not a separate line: a bare "89" next to a site
          name reads as a count of something. */}
      {prefix && (
        <span className="text-[12px] font-medium" style={{ color: 'var(--muted-fg)' }}>
          {prefix}
        </span>
      )}
      <span className="lm-num text-[14px] font-bold" style={{ color: 'var(--fg)' }}>
        {clamped}%
      </span>
      {!prefix && (
        <span className="text-[11.5px] font-medium" style={{ color: 'var(--muted-fg)' }}>
          {BAND_WORD[band]}
        </span>
      )}
    </span>
  );
}
